import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import { BeforeAfter, Gauge, TrendChart } from '../components/charts.js';
import { FullRescan } from '../components/FullRescan.js';
import { HelpButton } from '../components/HelpButton.js';
import { NotFound, PageTitle } from '../components/Layouts.js';
import { AREA_DETAILS, TileStatusIcon, useLocale } from '../components/site.js';
import { Card, LoadError, Loading, Notice, SeverityBadge } from '../components/ui.js';
import { errorMessage } from '../errors.js';

type AreaKey = keyof typeof AREA_DETAILS;
type Fix = Schemas['AreaFix'];

const isArea = (value: string): value is AreaKey => Object.hasOwn(AREA_DETAILS, value);

/**
 * One area of a site (T-35, owner's remarks 1 and 6): what we found, what it means for the business,
 * what to do and what it brings – in words and as "now → after" numbers.
 */
export function Area() {
  const { siteId = '', area = '' } = useParams();
  if (!isArea(area)) return <NotFound />;
  return <AreaPage siteId={siteId} area={area} />;
}

function AreaPage({ siteId, area }: { siteId: string; area: AreaKey }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const query = useQuery({
    queryKey: ['site', siteId, 'area', area, locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/areas/{area}', {
          params: { path: { siteId, area }, query: { locale } },
        }),
      ),
    refetchInterval: 60_000,
  });

  if (query.isPending) return <Loading />;
  if (query.isError)
    return (
      <LoadError message={errorMessage(query.error, t)} onRetry={() => void query.refetch()} />
    );
  const a = query.data;
  const score = a.score;

  return (
    <>
      <PageTitle actions={<TileStatusIcon status={a.tile.status} />}>
        {t(`site.tiles.${area}`)}
      </PageTitle>
      <p className="-mt-4 mb-6 max-w-prose text-slate-700">{a.why}</p>

      <div className="mb-6 grid gap-4 md:grid-cols-[auto_1fr]">
        <Card>
          <h2 className="mb-2 text-lg font-semibold">{t('area.score')}</h2>
          {score ? (
            <Gauge score={score.now} label={t('area.scoreSr', { score: score.now })} />
          ) : (
            <p className="max-w-56 text-slate-700">{t('area.notMeasured')}</p>
          )}
        </Card>
        <Card>
          <h2 className="mb-2 text-lg font-semibold">{t('area.trend')}</h2>
          <TrendChart points={a.trend} />
        </Card>
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <Card>
          <h2 className="mb-2 text-lg font-semibold">{t('area.now')}</h2>
          <p className="font-medium">{a.tile.headline}</p>
          {a.tile.sub ? <p className="mt-1 text-slate-700">{a.tile.sub}</p> : null}
          {a.fixes.length > 0 ? (
            <ul className="mt-2 list-disc pl-5">
              {a.fixes.map((f) => (
                <li key={fixKey(f)}>{f.explanation?.title ?? f.finding?.title}</li>
              ))}
            </ul>
          ) : null}
        </Card>
        <Card>
          <h2 className="mb-2 text-lg font-semibold">{t('area.means')}</h2>
          <p>{a.means}</p>
        </Card>
        <Card>
          <h2 className="mb-2 text-lg font-semibold">{t('area.todo')}</h2>
          {a.fixes.length === 0 ? (
            <p>{t('area.nothingToDo')}</p>
          ) : (
            <ol className="list-decimal pl-5">
              {a.fixes.map((f) => (
                <li key={fixKey(f)}>{f.explanation?.action ?? f.finding?.title}</li>
              ))}
            </ol>
          )}
        </Card>
        <Card>
          <h2 className="mb-2 text-lg font-semibold">{t('area.gain')}</h2>
          <p>{a.gain}</p>
          {score && score.after > score.now ? (
            <p className="mt-2 font-semibold">
              {t('area.gainNumbers', {
                now: score.now,
                after: score.after,
                siteNow: score.siteNow,
                siteAfter: score.siteAfter,
                gradeNow: score.gradeNow,
                gradeAfter: score.gradeAfter,
              })}
            </p>
          ) : null}
        </Card>
      </div>

      <section aria-labelledby="fixes-title" className="mb-6">
        <h2 id="fixes-title" className="mb-3 text-xl font-semibold">
          {t('area.problems')}
        </h2>
        {a.fixes.length === 0 ? (
          <Notice kind="success">{t('area.noProblems')}</Notice>
        ) : (
          <ul className="flex flex-col gap-3">
            {a.fixes.map((f) => (
              <li key={fixKey(f)}>
                <FixCard siteId={siteId} fix={f} now={score?.now ?? null} />
              </li>
            ))}
          </ul>
        )}
        <p className="mt-4">
          <Link
            to={`/sites/${siteId}${AREA_DETAILS[area]}`}
            className="font-semibold text-blue-800 underline"
          >
            {t('area.details')}
          </Link>
        </p>
      </section>

      <Card>
        <h2 className="mb-2 text-lg font-semibold">{t('rescan.title')}</h2>
        <FullRescan siteId={siteId} />
      </Card>
    </>
  );
}

const fixKey = (f: Fix) => f.finding?.id ?? f.incident?.id ?? '';

function FixCard({ siteId, fix, now }: { siteId: string; fix: Fix; now: number | null }) {
  const { t } = useTranslation();
  const e = fix.explanation;
  const severity = fix.finding?.severity ?? fix.incident?.severity;
  return (
    <Card>
      <h3 className="flex flex-wrap items-center gap-2 text-lg font-semibold">
        {severity ? <SeverityBadge severity={severity} /> : null}
        <span>{e?.title ?? fix.finding?.title}</span>
      </h3>
      {e ? (
        <dl className="mt-2 flex flex-col gap-2">
          <div>
            <dt className="font-semibold">{t('findings.what')}</dt>
            <dd>{e.what}</dd>
          </div>
          <div>
            <dt className="font-semibold">{t('findings.impact')}</dt>
            <dd>{e.impact}</dd>
          </div>
        </dl>
      ) : null}
      {now !== null && fix.areaAfter !== null && fix.areaAfter > now ? (
        <div className="mt-3">
          <p className="mb-1 text-sm font-semibold">{t('area.fixEffect')}</p>
          <BeforeAfter now={now} after={fix.areaAfter} />
        </div>
      ) : null}
      {fix.service ? (
        <div className="mt-3">
          <HelpButton
            siteId={siteId}
            service={fix.service}
            {...(fix.finding ? { findingId: fix.finding.id, ruleCode: fix.finding.ruleCode } : {})}
          />
        </div>
      ) : null}
    </Card>
  );
}
